/*
  Tanfolyam state refresh (op-course-main, op-course-card).
  Liquid already renders the right state, but pages can be served from cache, so the
  "now" used there may be stale. This re-checks date / deadline in shop time (Europe/Budapest)
  and only ever moves a course forward: open/soldout -> deadline -> past.

  Markup contract:
  - [data-op-course] data-state data-day-key="YYYYMMDD" data-close-key="YYYYMMDDHHMM"
  - [data-op-view="open soldout ..."] inside it: shown only for the listed states
  - [data-op-hide-past] / [data-op-hide-closed] on a list container: remove such cards
  - [data-op-list] + [data-op-empty] inside a group: empty message shown when no cards left
*/
(() => {
  // Several op-course sections can include this file on one page; run it once.
  if ( window.opCourseInit ) return;
  window.opCourseInit = true;

  const TIME_ZONE = 'Europe/Budapest';
  const RANK = { open: 0, soldout: 0, deadline: 1, past: 2 };

  const nowKeys = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIME_ZONE,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date());
    const get = type => parts.find(p => p.type === type).value;
    const day = `${get('year')}${get('month')}${get('day')}`;
    return { day: Number(day), minute: Number(`${day}${get('hour')}${get('minute')}`) };
  };

  const refresh = () => {
    const now = nowKeys();

    document.querySelectorAll('[data-op-course]').forEach(course => {
      const current = course.dataset.state || 'open';
      const dayKey = Number(course.dataset.dayKey) || 0;
      const closeKey = Number(course.dataset.closeKey) || 0;

      let state = current;
      if ( dayKey && dayKey < now.day ) {
        state = 'past';
      } else if ( closeKey && closeKey < now.minute ) {
        state = 'deadline';
      }
      if ( RANK[state] <= RANK[current] ) state = current;

      const group = course.closest('[data-op-group]');
      if (
        ( state === 'past' && course.closest('[data-op-hide-past]') ) ||
        ( state === 'deadline' && course.closest('[data-op-hide-closed]') )
      ) {
        course.remove();
        if ( group && ! group.querySelector('[data-op-course]') ) {
          const list = group.querySelector('[data-op-list]');
          const empty = group.querySelector('[data-op-empty]');
          if ( list ) list.hidden = true;
          if ( empty ) empty.hidden = false;
        }
        return;
      }

      if ( state === current ) return;
      course.dataset.state = state;
      course.querySelectorAll('[data-op-view]').forEach(view => {
        view.hidden = ! view.dataset.opView.split(' ').includes(state);
      });
    });
  };

  if ( document.readyState === 'loading' ) {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
  document.addEventListener('shopify:section:load', refresh);

  /*
    Smooth scroll to the course lists' in-page anchors (e.g. /pages/esemenyek#siklos).
    CSS scroll-behavior alone is skipped by some browsers / OS animation settings, so it is animated here.
    The target's scroll-margin-top (op-course-list.css) keeps it below a sticky header.
  */
  const smoothScrollTo = target => {
    const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const startY = window.scrollY;
    const endY = Math.max(0, target.getBoundingClientRect().top + startY - offset);
    const distance = endY - startY;
    const duration = Math.min(900, Math.max(400, Math.abs(distance) / 2));
    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    let start = null;

    const step = time => {
      if ( start === null ) start = time;
      const progress = Math.min(1, (time - start) / duration);
      window.scrollTo({ top: startY + distance * ease(progress), behavior: 'instant' });
      if ( progress < 1 ) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  document.addEventListener('click', event => {
    if ( event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ) return;
    const link = event.target.closest('a[href*="#"]');
    if ( ! link ) return;

    const url = new URL(link.href, window.location.href);
    if ( ! url.hash || url.pathname !== window.location.pathname ) return;

    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if ( ! target || ! target.matches('.op-course-list__group') ) return;

    event.preventDefault();
    history.pushState(null, '', url.hash);
    smoothScrollTo(target);
  });
})();
